import { Controller } from "@hotwired/stimulus";
import {
    applySidebarWidthPx,
    applyStoredSidebarWidth,
    clampSidebarWidthPx,
    readStoredSidebarWidthPx,
    SIDEBAR_WIDTH_MAX,
    SIDEBAR_WIDTH_MIN,
    SIDEBAR_WIDTH_STORAGE_KEY,
} from "../utils/sidebar_width_sync";

export {
    SIDEBAR_WIDTH_MAX,
    SIDEBAR_WIDTH_MIN,
    SIDEBAR_WIDTH_STORAGE_KEY,
} from "../utils/sidebar_width_sync";

export const SIDEBAR_WIDTH_DEFAULT = 232; // 14.5rem
const KEYBOARD_STEP = 16;

export default class extends Controller {
    static targets = ["handle"];

    connect() {
        applyStoredSidebarWidth();
        this.syncNarrowClass(this.widthForNarrowClass());
        document.addEventListener("depozen:sidebar-layout-change", this.boundSyncAfterLayoutChange);
    }

    start(event) {
        event.preventDefault();
        this.dragging = true;
        this.startX = event.clientX;
        this.startWidth = this.currentWidth();
        this.element.classList.add("app-shell--resizing");
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        document.addEventListener("pointermove", this.boundMove);
        document.addEventListener("pointerup", this.boundStop);
        document.addEventListener("pointercancel", this.boundStop);
    }

    disconnect() {
        this.stopDrag();
        document.removeEventListener("depozen:sidebar-layout-change", this.boundSyncAfterLayoutChange);
    }

    boundSyncAfterLayoutChange = () => {
        if (this.isCollapsed()) {
            this.element.classList.remove("app-shell--sidebar-narrow");
            return;
        }

        this.element.classList.remove("app-shell--sidebar-narrow");

        const apply = () => this.syncNarrowClass(this.widthForNarrowClass());

        requestAnimationFrame(() => {
            requestAnimationFrame(apply);
        });
        window.setTimeout(apply, 220);
    };

    widthForNarrowClass() {
        const measured = this.currentWidth();
        const stored = readStoredSidebarWidthPx() ?? SIDEBAR_WIDTH_DEFAULT;

        // Pendant l’animation collapsed → opened, la largeur mesurée reste ~4rem :
        // ne pas en déduire un mode narrow (masque le wordmark).
        if (measured <= 80) return stored;

        return measured;
    }

    boundMove = (event) => {
        if (!this.dragging) return;

        const rawWidth = this.startWidth + (event.clientX - this.startX);

        if (this.isCollapsed()) {
            if (rawWidth < this.expandAtWidth()) return;

            const width = this.clamp(rawWidth);
            this.setWidth(width, { persist: false, instant: true });
            this.expandFromDrag();
            return;
        }

        if (rawWidth < SIDEBAR_WIDTH_MIN) {
            if (!this.isCollapsed()) this.collapseFromDrag();
            return;
        }

        this.setWidth(this.clamp(rawWidth), { persist: false });
    };

    boundStop = () => {
        if (!this.dragging) return;

        this.dragging = false;
        this.element.classList.remove("app-shell--resizing");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        document.removeEventListener("pointermove", this.boundMove);
        document.removeEventListener("pointerup", this.boundStop);
        document.removeEventListener("pointercancel", this.boundStop);

        if (!this.isCollapsed()) {
            this.setWidth(this.currentWidth(), { persist: true });
        }
    };

    stopDrag() {
        if (!this.dragging) return;
        this.boundStop();
    }

    keydown(event) {
        if (!this.hasHandleTarget) return;

        if (this.isCollapsed()) {
            if (event.key === "ArrowRight" || event.key === "End") {
                event.preventDefault();
                this.expandFromDrag();
                const width =
                    event.key === "End"
                        ? SIDEBAR_WIDTH_MAX
                        : this.clamp(readStoredSidebarWidthPx() ?? SIDEBAR_WIDTH_DEFAULT);
                this.setWidth(width, { persist: true });
            }
            return;
        }

        let width = this.currentWidth();
        let handled = true;

        switch (event.key) {
            case "ArrowLeft":
                width -= KEYBOARD_STEP;
                break;
            case "ArrowRight":
                width += KEYBOARD_STEP;
                break;
            case "Home":
                width = SIDEBAR_WIDTH_MIN;
                break;
            case "End":
                width = SIDEBAR_WIDTH_MAX;
                break;
            default:
                handled = false;
        }

        if (!handled) return;

        event.preventDefault();
        this.setWidth(this.clamp(width), { persist: true });
    }

    reset(event) {
        event.preventDefault();
        applySidebarWidthPx(null, { persist: true });
        this.updateHandleAria(SIDEBAR_WIDTH_DEFAULT);
        this.syncNarrowClass(SIDEBAR_WIDTH_DEFAULT);
    }

    currentWidth() {
        const sidebar = this.element.querySelector(".app-shell__sidebar");
        return sidebar?.getBoundingClientRect().width ?? SIDEBAR_WIDTH_DEFAULT;
    }

    expandAtWidth() {
        return SIDEBAR_WIDTH_MIN;
    }

    clamp(width) {
        return clampSidebarWidthPx(width);
    }

    setWidth(px, { persist, instant = !persist }) {
        const clamped = applySidebarWidthPx(px, { persist, instant });
        if (clamped != null) {
            this.updateHandleAria(clamped);
            this.syncNarrowClass(clamped);
        }
    }

    syncNarrowClass(width) {
        if (this.isCollapsed()) {
            this.element.classList.remove("app-shell--sidebar-narrow");
            return;
        }

        this.element.classList.toggle("app-shell--sidebar-narrow", width <= SIDEBAR_WIDTH_MIN);
    }

    collapseFromDrag() {
        this.element.classList.remove("app-shell--sidebar-narrow");
        this.element.dispatchEvent(
            new CustomEvent("depozen:sidebar-collapse-from-resize", { bubbles: false }),
        );
    }

    expandFromDrag() {
        this.element.dispatchEvent(
            new CustomEvent("depozen:sidebar-expand-from-resize", { bubbles: false }),
        );
    }

    updateHandleAria(width) {
        if (!this.hasHandleTarget) return;

        const resolved = width ?? readStoredSidebarWidthPx() ?? SIDEBAR_WIDTH_DEFAULT;
        this.handleTarget.setAttribute("aria-valuenow", String(resolved));
        this.handleTarget.setAttribute("aria-valuemin", String(SIDEBAR_WIDTH_MIN));
        this.handleTarget.setAttribute("aria-valuemax", String(SIDEBAR_WIDTH_MAX));
    }

    isCollapsed() {
        return (
            document.documentElement.dataset.sidebarLayout === "collapsed" ||
            this.element.dataset.sidebarLayout === "collapsed"
        );
    }
}
